import memoize from 'memoize-one';
import { createRef, CSSProperties, memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { areEqual, FixedSizeList as List } from 'react-window';
import { reduxForm } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { INQUIRY_TEMPLATE_SCREEN_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  ScreenDimensions,
  StepPreviewMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ComponentDragPreview } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentDragPreview';
import { ScreenEditorDroppableScreen } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenEditorDroppableScreen';
import { SelectedComponentAndStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import { SelectedStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import { ScreenItemConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import { SKIPPED_PAGE_REASONS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import InquiryTemplateScreenPreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  InquiryTemplateVersionStep,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { RootState } from 'dashboard/redux/store';
import { FieldSchema } from 'lib/fields';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { UiComponentConfig } from 'lib/interfaces/ui-components';

const PREVIEW_PADDING = 160;

type ScreenPreviewItem = {
  displayName: string;
  stepConfig: InquiryTemplateVersionStep | undefined;
  stepPreview: StepPreview;
  page: React.ReactElement;
  skipReason: string | undefined;
  selected: boolean;
  onComponentClick: (componentAndStep: SelectedComponentAndStep) => void;
  onStepSelected: () => void;
};

type ScreenPreviewData = {
  screenPreviewItems: ScreenPreviewItem[];
  fieldSchema: FieldSchema[];
  initialFieldSchema: FieldSchema[];
  selectedComponentAndStep: SelectedComponentAndStep;
  theme: ThemeAttributes;
  screenDimensions: ScreenDimensions;
  editable: boolean;
  startedComponentDrag: boolean;
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
  }) => void;
};

const ScreenPreview = memo(
  ({ data, index, style }: { data: ScreenPreviewData; index: number; style: CSSProperties }) => {
    const {
      screenPreviewItems,
      fieldSchema,
      initialFieldSchema,
      selectedComponentAndStep,
      theme,
      screenDimensions,
      editable,
      startedComponentDrag,
      updateSteps,
    } = data;

    // Hides preview for inquiry loop steps, as the screen cannot be edited
    if (screenPreviewItems.length <= index) {
      return <></>;
    }

    const {
      displayName,
      stepConfig,
      stepPreview,
      page,
      skipReason,
      selected,
      onComponentClick,
      onStepSelected,
    } = screenPreviewItems[index];

    const isUiStepScreen = isUiStepConfig(stepConfig);
    const previewStyles = {
      ...style,
      display: 'flex',
      justifyContent: 'center',
      height: 'fit-content',
    };

    return (
      <div style={previewStyles}>
        <ScreenFlowNodeWrapper
          data-name={`${page.key}`}
          data-test={`${displayName}__screen-preview`}
          data-marketing-id='ite-screen-editor__preview'
          title={displayName}
          stepStatuses={
            (stepConfig?.config.statuses.map((status) => status.name) as InquiryStatus[]) || []
          }
          node={
            <ScreenEditorDroppableScreen
              step={isUiStepScreen ? stepConfig : undefined}
              fieldSchema={fieldSchema}
              initialFieldSchema={initialFieldSchema}
              selected={selected}
              selectedComponentAndStep={selectedComponentAndStep}
              theme={theme}
              editable={editable}
              startedComponentDrag={startedComponentDrag}
              isUiStepScreen={isUiStepScreen}
              stepPreview={stepPreview}
              onComponentClick={onComponentClick}
              updateSteps={updateSteps}
              {...(!isUiStepScreen && {
                fallbackScreen: (
                  <InquiryTemplateScreenPreview
                    step={stepPreview}
                    page={page}
                    theme={theme}
                    skipReason={skipReason}
                    screenDimensions={screenDimensions}
                    selected={selected}
                  />
                ),
              })}
            />
          }
          editable={false}
          handleNodeClick={onStepSelected}
          handleStickerClick={onStepSelected}
          withSticker
        />
      </div>
    );
  },
  areEqual,
);

ScreenPreview.displayName = 'ScreenPreview';

const createItemData = memoize(
  (
    screenItemConfigs: ScreenItemConfig[],
    stepsConfigMap: Record<string, InquiryTemplateVersionStep>,
    stepsPreviewMap: StepPreviewMap,
    previewPageMap: Record<string, React.ReactElement>,
    fieldSchema: FieldSchema[],
    initialFieldSchema: FieldSchema[],
    selectedStep: SelectedStep | undefined,
    selectedComponentAndStep: SelectedComponentAndStep,
    theme: ThemeAttributes,
    screenDimensions: ScreenDimensions,
    editable: boolean,
    startedComponentDrag: boolean,
    onComponentClick: (componentAndStep: SelectedComponentAndStep) => void,
    onStepSelected: (screenItemConfig: ScreenItemConfig) => void,
    updateSteps: ({
      updatedStep,
      component,
    }: {
      updatedStep: InquiryTemplateVersionStep;
      component?: UiComponentConfig;
    }) => void,
  ): ScreenPreviewData => {
    const screenPreviewItems = screenItemConfigs.map((screenItemConfig) => {
      const { displayName, key, stepToken } = screenItemConfig;
      const stepConfig = stepsConfigMap[stepToken];
      const selected = key === selectedStep?.key && stepToken === selectedStep?.stepToken;
      let skipReason;
      if (
        screenItemConfig.skip &&
        screenItemConfig.pageKeys?.[0] != null &&
        stepConfig?.type != null
      ) {
        skipReason = SKIPPED_PAGE_REASONS[stepConfig.type]?.[screenItemConfig.pageKeys[0]];
      }
      return {
        displayName,
        stepConfig,
        stepPreview: stepsPreviewMap[stepToken],
        page: previewPageMap[key],
        skipReason,
        selected,
        onComponentClick: (componentAndStep: SelectedComponentAndStep) => {
          if (componentAndStep?.step.name !== stepConfig.name) {
            onStepSelected(screenItemConfig);
          }
          onComponentClick(componentAndStep);
        },
        onStepSelected: () => onStepSelected(screenItemConfig),
      };
    });
    return {
      screenPreviewItems,
      fieldSchema,
      initialFieldSchema,
      selectedComponentAndStep,
      theme,
      screenDimensions,
      editable,
      startedComponentDrag,
      updateSteps,
    };
  },
);

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  width: 100%;
  height: 100%;
`;

const PreviewWrapper = styled.div`
  height: 100%;
  justify-content: center;
  width: fit-content;
`;

interface ScreenPreviewsProps {
  screenItemConfigs: ScreenItemConfig[];
  stepsConfigMap: Record<string, InquiryTemplateVersionStep>;
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: Record<string, React.ReactElement>;
  fieldSchema: FieldSchema[];
  selectedStep: SelectedStep | undefined;
  selectedComponentAndStep: SelectedComponentAndStep;
  theme: ThemeAttributes;
  screenDimensions: ScreenDimensions;
  onComponentClick: (componentAndStep: SelectedComponentAndStep) => void;
  onStepSelected: (screenItemConfig: ScreenItemConfig) => void;
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
  }) => void;
}

const ScreenPreviews = ({
  screenItemConfigs,
  stepsConfigMap,
  stepsPreviewMap,
  previewPageMap,
  fieldSchema,
  selectedStep,
  selectedComponentAndStep,
  theme,
  screenDimensions,
  onComponentClick,
  onStepSelected,
  updateSteps,
}: ScreenPreviewsProps) => {
  const editableUngated = useIsEditableWithOptions();
  const [startedComponentDrag, setStartedComponentDrag] = useState(false);
  const [listDimensions, setListDimensions] = useState<{ height: number; width: number }>({
    height: screenDimensions.height + PREVIEW_PADDING, // default height to single preview
    width: PREVIEW_WIDTH,
  });

  const initialFieldSchema = useSelector<RootState, FieldSchema[]>(
    (state) =>
      state.inquiryTemplate?.inquiryTemplateTreeData.inquiryTemplateVersion.attributes[
        'field-schema'
      ] ?? [],
  );

  const containerRef = createRef<HTMLDivElement>();
  const listRef = createRef<List>();

  useEffect(() => {
    if (containerRef.current != null) {
      const { offsetWidth, offsetHeight } = containerRef.current;
      setListDimensions({ height: offsetHeight, width: offsetWidth });
    }
  }, []);

  useEffect(() => {
    if (listRef.current) {
      const activePreviewIndex = screenItemConfigs.findIndex(
        ({ key }) => key === selectedStep?.key,
      );
      listRef.current.scrollToItem(activePreviewIndex, 'center');
    }
  }, [selectedStep]);

  const itemData: ScreenPreviewData = createItemData(
    screenItemConfigs,
    stepsConfigMap,
    stepsPreviewMap,
    previewPageMap,
    fieldSchema,
    initialFieldSchema,
    selectedStep,
    selectedComponentAndStep,
    theme,
    screenDimensions,
    editableUngated,
    startedComponentDrag,
    onComponentClick,
    onStepSelected,
    updateSteps,
  );

  return (
    <PreviewContainer ref={containerRef}>
      <PreviewWrapper>
        <List
          ref={listRef}
          height={listDimensions.height}
          width={listDimensions.width}
          itemCount={Object.keys(previewPageMap).length}
          itemData={itemData}
          itemSize={screenDimensions.height + PREVIEW_PADDING}
        >
          {ScreenPreview}
        </List>
      </PreviewWrapper>
      <ComponentDragPreview setDragStatus={setStartedComponentDrag} />
    </PreviewContainer>
  );
};

export default reduxForm<Record<string, never>, ScreenPreviewsProps>({
  form: INQUIRY_TEMPLATE_SCREEN_EDITOR_FORM_NAME,
})(ScreenPreviews);
